import { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Bookmark, BookmarkPlus, X, Users } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { savedFiltersService, SavedFilter } from '@/services/saved-filters.service';

interface Props {
  /** Scope key — drives which saved-filter rows we show. e.g. 'leads', 'deals'. */
  scope: string;
  /**
   * Current filter object — saved verbatim when the user clicks "Save current view".
   * Only persisted if the user opts in; nothing happens automatically.
   */
  currentFilter: Record<string, unknown>;
  /**
   * Whether the current filter has any non-default values worth saving.
   * The "Save current view" CTA only appears when this is true.
   */
  hasActiveFilter: boolean;
  /**
   * Apply a saved filter — receives the filter JSON to restore. The page is
   * responsible for translating fields back into its local state.
   */
  onApply: (filter: Record<string, unknown>) => void;
  /** Reset the page to its default (empty filter) state. */
  onClear: () => void;
}

/**
 * Reusable "Saved views" chip row + save modal — works on any list/kanban
 * page. Persists arbitrary filter JSON via SavedFilter (scope-keyed). Owner-
 * scoped by default; toggle 'Share with team' to expose to the whole company.
 */
export function SavedViewsBar({ scope, currentFilter, hasActiveFilter, onApply, onClear }: Props) {
  const [filters, setFilters] = useState<SavedFilter[]>([]);
  const [activeUid, setActiveUid] = useState<string | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);

  useEffect(() => {
    savedFiltersService.list(scope).then(setFilters).catch(() => undefined);
  }, [scope]);

  const apply = (f: SavedFilter) => {
    setActiveUid(f.filterUid);
    onApply((f.filter ?? {}) as Record<string, unknown>);
  };

  const clear = () => {
    setActiveUid(null);
    onClear();
  };

  const onSave = async () => {
    if (!saveName.trim()) return;
    try {
      const created = await savedFiltersService.create({
        scope,
        name: saveName.trim(),
        filter: currentFilter,
        isShared: saveShared,
      });
      setFilters((prev) => [created, ...prev]);
      setActiveUid(created.filterUid);
      setSaveOpen(false);
      setSaveName('');
      setSaveShared(false);
      toast.success('View saved');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to save view';
      toast.error(msg);
    }
  };

  const onDelete = async (uid: string) => {
    try {
      await savedFiltersService.remove(uid);
      setFilters((prev) => prev.filter((f) => f.filterUid !== uid));
      if (activeUid === uid) setActiveUid(null);
    } catch { toast.error('Failed to delete view'); }
  };

  if (filters.length === 0 && !hasActiveFilter) return null;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-text-muted">
          <Bookmark size={12} /> Saved views
        </span>
        {filters.map((f) => (
          <span
            key={f.filterUid}
            className={clsx(
              'group inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition',
              activeUid === f.filterUid
                ? 'border-primary bg-primary-soft text-primary'
                : 'border-border bg-surface text-text-secondary hover:bg-surface-alt cursor-pointer',
            )}
          >
            <button onClick={() => apply(f)} className="inline-flex items-center gap-1.5">
              {f.name}
              {f.isShared && <Users size={10} className="opacity-60" />}
            </button>
            <button
              onClick={() => onDelete(f.filterUid)}
              className="opacity-0 group-hover:opacity-100 hover:text-danger transition"
              aria-label="Delete view"
            >
              <X size={11} />
            </button>
          </span>
        ))}
        {hasActiveFilter && !activeUid && (
          <button
            onClick={() => setSaveOpen(true)}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-primary/50 bg-primary-soft/40 px-3 py-1 text-xs font-semibold text-primary hover:bg-primary-soft"
          >
            <BookmarkPlus size={12} /> Save current view
          </button>
        )}
        {activeUid && (
          <button
            onClick={clear}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-3 py-1 text-xs font-medium text-text-muted hover:bg-surface-alt"
          >
            <X size={11} /> Clear view
          </button>
        )}
      </div>

      <Modal open={saveOpen} onClose={() => setSaveOpen(false)} title="Save current view">
        <div className="space-y-3">
          <Input
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="View name (e.g. ‘Hot leads — UAE’)"
            autoFocus
          />
          <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={saveShared}
              onChange={(e) => setSaveShared(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            Share with my team
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="secondary" onClick={() => setSaveOpen(false)}>Cancel</Button>
          <Button disabled={!saveName.trim()} onClick={onSave}>Save view</Button>
        </div>
      </Modal>
    </>
  );
}
